import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalHeader, PortalBody, StatTile, StatusPill, EmptyState } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PackagePlus, Download, ArrowRight } from "lucide-react";
import DemoBanner from "@/components/DemoBanner";
import StandardSeal from "@/components/StandardSeal";
import StandardBadgeWidget from "@/components/StandardBadgeWidget";

export function ClientDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alignmentStatus, setAlignmentStatus] = useState(null);
  const loadOrders = useCallback(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => {
    api.get("/alignment/status/mine").then((r) => setAlignmentStatus(r.data)).catch(() => {});
  }, []);
  const open = orders.filter((o) => !["delivered","cancelled"].includes(o.status));
  const delivered = orders.filter((o) => o.status === "delivered");

  return (
    <>
      <PortalHeader eyebrow={`/ CLIENT · ${user?.org_name || ""}`} title="Command Post" testId="client-dashboard-header">
        <Link to="/client/orders/new" className="dt6-btn dt6-btn-primary" data-testid="client-dashboard-new-order">
          <PackagePlus size={14} /> Start a new order
        </Link>
      </PortalHeader>
      <PortalBody>
        {alignmentStatus?.approved && (
          <>
            <div className="dt6-card p-4 mb-6 flex items-center gap-4" style={{ borderColor: "var(--dt6-primary)" }} data-testid="standard-verified-badge">
              <StandardSeal size={54} />
              <div>
                <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-primary)]">/ DT6 STANDARD · VERIFIED</div>
                <div className="text-sm">
                  Approved{alignmentStatus.decided_at ? ` · ${alignmentStatus.decided_at.slice(0, 10)}` : ""}
                </div>
              </div>
            </div>
            <StandardBadgeWidget badgeSlug={alignmentStatus.badge_slug} orgName={alignmentStatus.org_name} />
          </>
        )}
        <DemoBanner onDelivered={loadOrders} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatTile label="Open orders" value={open.length} hint="in review / in progress" testId="client-stat-open" />
          <StatTile label="Delivered" value={delivered.length} hint="final deliverables ready" testId="client-stat-delivered" />
          <StatTile label="Total lifetime" value={orders.length} testId="client-stat-total" />
        </div>

        <h2 className="font-heading text-xl font-bold mb-3">Recent orders</h2>
        {loading ? (
          <EmptyState testId="client-orders-loading">LOADING...</EmptyState>
        ) : orders.length === 0 ? (
          <EmptyState testId="client-orders-empty">
            NO ORDERS YET. START WITH THE <Link to="/client/orders/new" className="dt6-link ml-1">NEW ORDER</Link> FLOW.
          </EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead>
                <tr>
                  <th>Order</th><th>Module</th><th>Status</th><th>Created</th><th>Deliverables</th><th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`client-order-row-${o.id}`}>
                    <td className="font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</td>
                    <td><span className="font-mono">{o.module_code}</span></td>
                    <td><StatusPill status={o.status} /></td>
                    <td className="font-mono text-xs text-[color:var(--dt6-muted)]">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td>{o.deliverables?.length || 0}</td>
                    <td><Link to="/client/deliverables" className="dt6-link text-xs">view</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

const MODULE_TYPES = {
  D1: ["Voter file pull", "Consumer overlay", "Digital audience"],
  D2: ["NCOA/PCOA", "Dedupe + deceased", "Phone / email append", "Ballot cure"],
  D3: ["Visitor ID", "CRM enrichment", "Identity graph match"],
  D4: ["Turnout scoring", "Persuasion scoring", "Donor propensity"],
  D5: ["Lead routing setup", "Ping/post integration"],
  D6: ["Meta/Google audience", "P2P text send", "Direct mail trigger", "Geofence audience"],
};

export function ClientNewOrder({ role = "client" }) {
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [module_code, setModule] = useState("");
  const [order_type, setOrderType] = useState("");
  const [notes, setNotes] = useState("");
  const [state, setState] = useState("");
  const [record_count, setRecordCount] = useState("");
  const [priority, setPriority] = useState("standard");
  const [on_behalf_of_email, setBehalf] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/modules").then((r) => setModules(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        module_code,
        order_type: order_type || (MODULE_TYPES[module_code]?.[0] || "custom"),
        parameters: {
          state,
          est_record_count: record_count ? Number(record_count) : undefined,
          priority,
        },
        notes,
      };
      if (role === "partner" && on_behalf_of_email) payload.on_behalf_of_email = on_behalf_of_email;
      const { data } = await api.post("/orders", payload);
      nav(role === "partner" ? "/partner" : "/client", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PortalHeader eyebrow={role === "partner" ? "/ PARTNER" : "/ CLIENT"} title="Submit new order" testId="new-order-header" />
      <PortalBody>
        <form onSubmit={submit} className="dt6-card p-8 max-w-3xl" data-testid="new-order-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="dt6-label">Module</label>
              <select className="dt6-select" required value={module_code} onChange={(e) => { setModule(e.target.value); setOrderType(""); }} data-testid="order-module">
                <option value="">Select…</option>
                {modules.map((m) => (
                  <option key={m.code} value={m.code}>{m.code} — {m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="dt6-label">Order type</label>
              <select className="dt6-select" value={order_type} onChange={(e)=>setOrderType(e.target.value)} disabled={!module_code} data-testid="order-type">
                <option value="">Default (first)</option>
                {(MODULE_TYPES[module_code] || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="dt6-label">State / geography</label>
              <input className="dt6-input" value={state} onChange={(e)=>setState(e.target.value)} data-testid="order-state" />
            </div>
            <div>
              <label className="dt6-label">Est. record count</label>
              <input className="dt6-input" type="number" value={record_count} onChange={(e)=>setRecordCount(e.target.value)} data-testid="order-record-count" />
            </div>
            <div>
              <label className="dt6-label">Priority</label>
              <select className="dt6-select" value={priority} onChange={(e)=>setPriority(e.target.value)} data-testid="order-priority">
                <option value="standard">Standard</option>
                <option value="rush">Rush</option>
              </select>
            </div>
            {role === "partner" && (
              <div>
                <label className="dt6-label">Client email (on behalf of)</label>
                <input className="dt6-input" type="email" value={on_behalf_of_email} onChange={(e)=>setBehalf(e.target.value)} data-testid="order-behalf" />
              </div>
            )}
            <div className="md:col-span-2">
              <label className="dt6-label">Notes</label>
              <textarea className="dt6-textarea" rows={4} value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="order-notes" />
            </div>
          </div>
          {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]" data-testid="new-order-error">{err}</div>}
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">
              / ORDERS ROUTE TO INTERNAL FULFILLMENT QUEUE
            </div>
            <button type="submit" className="dt6-btn dt6-btn-primary" disabled={busy || !module_code} data-testid="new-order-submit">
              {busy ? "SUBMITTING..." : "Submit order"} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </PortalBody>
    </>
  );
}

export function ClientDeliverables() {
  const [orders, setOrders] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/orders/mine").then((r) => setOrders(r.data)); }, []);
  const withDeliverables = orders.filter((o) => (o.deliverables || []).length > 0);

  const download = async (orderId, d) => {
    setBusyId(d.id); setErr(null);
    try {
      // Ask backend for a short-lived signed URL scoped to this user.
      const { data } = await api.post(`/orders/${orderId}/deliverables/${d.id}/signed-url`);
      // Fetch the actual bytes with the same auth header so nothing sensitive
      // ever hits browser history / referer chains.
      const blobResp = await api.get(data.url.replace(/^\/api/, ""), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([blobResp.data], { type: d.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || `deliverable-${d.id}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PortalHeader eyebrow="/ CLIENT" title="Deliverables" testId="client-deliv-header" />
      <PortalBody>
        {err && (
          <div className="dt6-card p-3 mb-4 text-sm text-[color:var(--dt6-danger)]" data-testid="client-deliv-err">{err}</div>
        )}
        {withDeliverables.length === 0 ? (
          <EmptyState testId="client-deliv-empty">NO DELIVERABLES YET.</EmptyState>
        ) : (
          <div className="space-y-4">
            {withDeliverables.map((o) => (
              <div key={o.id} className="dt6-card p-6" data-testid={`client-deliv-order-${o.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-heading text-lg font-bold">{o.module_code} · {o.order_type}</div>
                    <div className="font-mono text-xs text-[color:var(--dt6-muted)]">ORDER {o.id.slice(0,8).toUpperCase()}</div>
                  </div>
                  <StatusPill status={o.status} />
                </div>
                <div className="grid gap-2">
                  {o.deliverables.map((d) => {
                    const notReady = !d.storage_path;
                    return (
                    <div key={d.id} className="flex items-center justify-between border-t pt-2" style={{ borderColor: "var(--dt6-border)" }}>
                      <div className="flex items-center gap-3">
                        <Download size={14} strokeWidth={1.5} className="text-[color:var(--dt6-primary)]" />
                        <span className="text-sm">{d.label}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-xs text-[color:var(--dt6-muted)]">{d.filename} · {d.size_kb} KB</span>
                        {notReady ? (
                          <span
                            className="font-mono text-[0.65rem] tracking-[0.12em] uppercase text-[color:var(--dt6-muted)] italic"
                            data-testid={`client-deliv-notready-${d.id}`}
                            title="This deliverable has no file blob yet — it may be legacy or still uploading."
                          >
                            file not yet available
                          </span>
                        ) : (
                        <button
                          type="button"
                          className="dt6-btn dt6-btn-ghost text-xs"
                          disabled={busyId === d.id}
                          onClick={() => download(o.id, d)}
                          data-testid={`client-deliv-download-${d.id}`}
                        >
                          {busyId === d.id ? "PREPARING…" : "DOWNLOAD"}
                        </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function ClientAudits() {
  return (
    <>
      <PortalHeader eyebrow="/ CLIENT" title="My audits" testId="client-audits-header" />
      <PortalBody>
        <EmptyState testId="client-audits-empty">
          AUDIT HISTORY LINKED TO SIGNED-IN EMAIL WILL APPEAR HERE.
          <div className="mt-3">
            <Link to="/audit" className="dt6-link">Request a new audit →</Link>
          </div>
        </EmptyState>
      </PortalBody>
    </>
  );
}

export function ClientAccount() {
  const { user } = useAuth();
  const [form, setForm] = useState({ full_name: user?.name || "", email: user?.email || "", request_type: "delete", state: "", details: "" });
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setOk(false);
    try { await api.post("/client/dsar", form); setOk(true); } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <>
      <PortalHeader eyebrow="/ CLIENT" title="Account" testId="client-account-header" />
      <PortalBody>
        <div className="dt6-card p-6 max-w-2xl mb-6">
          <div className="dt6-chip mb-3">/ PROFILE</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="dt6-label">Name</div><div>{user?.name}</div></div>
            <div><div className="dt6-label">Email</div><div>{user?.email}</div></div>
            <div><div className="dt6-label">Role</div><div className="font-mono uppercase">{user?.role}</div></div>
            <div><div className="dt6-label">Organization</div><div>{user?.org_name || "—"}</div></div>
          </div>
        </div>
        <div className="dt6-card p-6 max-w-2xl">
          <div className="dt6-chip mb-3">/ REQUEST MY DATA</div>
          <p className="text-sm text-[color:var(--dt6-muted)] mb-4">
            Submit a data-subject request. We respond within 30 days per applicable law.
          </p>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="client-dsar-form">
            <div><label className="dt6-label">Full name</label><input className="dt6-input" required value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})} data-testid="client-dsar-name" /></div>
            <div><label className="dt6-label">Email</label><input className="dt6-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="client-dsar-email" /></div>
            <div><label className="dt6-label">Request type</label>
              <select className="dt6-select" value={form.request_type} onChange={(e)=>setForm({...form,request_type:e.target.value})} data-testid="client-dsar-type">
                <option value="access">Access</option><option value="delete">Delete</option><option value="correct">Correct</option>
              </select>
            </div>
            <div><label className="dt6-label">State</label><input className="dt6-input" value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})} data-testid="client-dsar-state" /></div>
            <div className="md:col-span-2"><label className="dt6-label">Details</label><textarea className="dt6-textarea" rows={4} value={form.details} onChange={(e)=>setForm({...form,details:e.target.value})} data-testid="client-dsar-details" /></div>
            {ok && <div className="md:col-span-2 text-sm text-[color:var(--dt6-success)]" data-testid="client-dsar-success">REQUEST RECEIVED. YOU WILL BE CONTACTED WITHIN 30 DAYS.</div>}
            {err && <div className="md:col-span-2 text-sm text-[color:var(--dt6-danger)]" data-testid="client-dsar-error">{err}</div>}
            <div className="md:col-span-2 flex justify-end"><button className="dt6-btn dt6-btn-primary" data-testid="client-dsar-submit">Submit request</button></div>
          </form>
        </div>
      </PortalBody>
    </>
  );
}
